/**
 * Collection jump navigation: smooth scroll with header + sticky bar offset,
 * and scroll-synced active link state.
 */
function parseCssPx(value) {
  const n = parseFloat(String(value ?? '').trim());
  return Number.isFinite(n) ? n : 0;
}

function scrollBehavior() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
}

/** Subpixel / rounding: section top must be within this many px of the activation line to count. */
const ACTIVATION_TOP_TOLERANCE_PX = 2;

/**
 * Nudge scroll target slightly past the line so the correct tab activates without a manual 1–2px scroll.
 */
const SCROLL_TARGET_NUDGE_PX = 2;

/**
 * @param {HTMLElement} root
 */
function initShopCollectionJumpNav(root) {
  const nav = root.querySelector('[data-jump-nav-list]');
  const links = nav ? [...nav.querySelectorAll('a[href^="#"]')] : [];
  if (!nav || links.length === 0) return () => {};

  const sectionIds = links
    .map((a) => {
      const href = a.getAttribute('href');
      return href && href.length > 1 ? href.slice(1) : '';
    })
    .filter(Boolean);

  const stickyInner = root.querySelector('[data-jump-nav-sticky]');

  /** Matches CSS: sticky header uses top:-1px, jump nav uses --header-height - 1px. */
  const getHeaderHeight = () => {
    const headerEl = document.querySelector('header-component');
    if (headerEl instanceof HTMLElement && headerEl.hasAttribute('sticky')) {
      const h = parseCssPx(getComputedStyle(document.body).getPropertyValue('--header-height'));
      return Math.max(0, h - 1);
    }
    return 0;
  };

  const getActivationY = () => {
    const header = getHeaderHeight();
    const bar = stickyInner?.offsetHeight ?? root.offsetHeight ?? 0;
    return header + bar + 8;
  };

  const setActive = (id) => {
    for (const link of links) {
      const href = link.getAttribute('href');
      const linkId = href && href.length > 1 ? href.slice(1) : '';
      const isActive = linkId === id;
      link.classList.toggle('shop-collection-jump-nav__link--active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    }
  };

  const syncFromScroll = () => {
    const y = getActivationY();
    /** @type {string | null} */
    let current = null;

    for (const id of sectionIds) {
      const el = document.getElementById(id);
      if (!el || !(el instanceof HTMLElement)) continue;
      const top = el.getBoundingClientRect().top;
      if (top <= y + ACTIVATION_TOP_TOLERANCE_PX) current = id;
    }

    // At scroll top no rail crosses the activation line yet (all anchors sit below y).
    if (!current) {
      current =
        sectionIds.find((id) => document.getElementById(id) instanceof HTMLElement) ?? null;
    }

    if (current) setActive(current);
  };

  const scrollToTarget = (id) => {
    const el = document.getElementById(id);
    if (!el || !(el instanceof HTMLElement)) return;

    const activation = getActivationY();
    const y =
      el.getBoundingClientRect().top + window.scrollY - activation + SCROLL_TARGET_NUDGE_PX;
    window.scrollTo({ top: Math.max(0, y), behavior: scrollBehavior() });
    setActive(id);

    const behavior = scrollBehavior();
    const finalize = () => {
      setActive(id);
      syncFromScroll();
    };

    if (behavior === 'smooth') {
      let settled = false;
      const runOnce = () => {
        if (settled) return;
        settled = true;
        finalize();
      };
      window.addEventListener('scrollend', runOnce, { once: true });
      window.setTimeout(runOnce, 550);
    } else {
      queueMicrotask(finalize);
    }
  };

  const onLinkClick = (event) => {
    const anchor = event.currentTarget;
    if (!(anchor instanceof HTMLAnchorElement)) return;
    const href = anchor.getAttribute('href');
    if (!href || href.length < 2 || href.charAt(0) !== '#') return;
    const id = href.slice(1);
    const target = document.getElementById(id);
    if (!target) return;
    event.preventDefault();
    scrollToTarget(id);
    history.pushState(null, '', href);
  };

  for (const link of links) {
    link.addEventListener('click', onLinkClick);
  }

  window.addEventListener('scroll', syncFromScroll, { passive: true });
  window.addEventListener('resize', syncFromScroll);

  const onHash = () => {
    const hash = window.location.hash;
    if (hash.length > 1) {
      const id = hash.slice(1);
      if (sectionIds.includes(id)) {
        scrollToTarget(id);
        return;
      }
    }
    syncFromScroll();
  };

  window.addEventListener('hashchange', onHash);
  queueMicrotask(onHash);

  return () => {
    for (const link of links) {
      link.removeEventListener('click', onLinkClick);
    }
    window.removeEventListener('scroll', syncFromScroll);
    window.removeEventListener('resize', syncFromScroll);
    window.removeEventListener('hashchange', onHash);
  };
}

class ShopCollectionJumpNav extends HTMLElement {
  /** @type {(() => void) | undefined} */
  #dispose;

  connectedCallback() {
    this.#dispose = initShopCollectionJumpNav(this);
  }

  disconnectedCallback() {
    this.#dispose?.();
    this.#dispose = undefined;
  }
}

if (!customElements.get('shop-collection-jump-nav')) {
  customElements.define('shop-collection-jump-nav', ShopCollectionJumpNav);
}
